import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, Validators, Form } from '@angular/forms';
import { MediaObserver, MediaChange } from '@angular/flex-layout';
import { Router, ActivatedRoute } from '@angular/router';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';

import { UtilityService } from 'src/app/services/utility.service';
import { UserService } from 'src/app/services/user.service';
import { ApplicationService } from '../../services/application.service';
import { RegistrationService } from '../../services/registration.service';
import { CompanyService } from '../../services/company.service';

import { MessageListItem } from '../../models/application';
import { ForgotPasswordInfo } from '../../models/registration';

@Component({
  selector: 'app-login-one-time',
  templateUrl: './login-one-time.component.html',
  styleUrls: ['./login-one-time.component.scss']
})
export class LoginOneTimeComponent implements OnInit {
  private _handleError: HandleError;
  private _logging = environment.logging;
  fieldValidations = environment.fieldValidations;

  recaptchaSiteKey = environment.restAPI.recaptchaSiteKey;

  // isBrowserIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
  isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

  showInstructionMessagesSpinner = false;
  instructionMessageList: MessageListItem[] = [];

  loginOneTimeForm: UntypedFormGroup;
  userNameFormControl = new UntypedFormControl();
  temporaryPasswordFormControl = new UntypedFormControl();
  newPasswordFormControl = new UntypedFormControl();
  confirmNewPasswordFormControl = new UntypedFormControl();
  newPasswordEqualsFormControl = new UntypedFormControl();
  recaptchaFormControl = new UntypedFormControl();

  forgotPasswordInfo: ForgotPasswordInfo;

  showSubmitSpinner = false;
  showTemporaryPasswordError = false;
  showExpiredTemporaryPasswordError = false;
  showSubmitError = false;
  showLoginButton = false;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _formBuilder: UntypedFormBuilder,
    private _errorHandler: ErrorHandler,
    private _utilityService: UtilityService,
    private _userService: UserService,
    private _applicationService: ApplicationService,
    private _registrationService: RegistrationService,
    private _companyService: CompanyService,
    public media: MediaObserver
  ) {
    this._handleError = _errorHandler.createHandleError('LoginOneTimeComponent');
    this.forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();
    this.createForm();
    this.scrollToTop();
  }

  ngOnInit() {}

  createForm(): void {
    this.loginOneTimeForm = this._formBuilder.group({});
    this.loginOneTimeForm.addControl('userNameFormControl', this.userNameFormControl);
    this.loginOneTimeForm.addControl('temporaryPasswordFormControl', this.temporaryPasswordFormControl);
    this.loginOneTimeForm.addControl('newPasswordFormControl', this.newPasswordFormControl);
    this.loginOneTimeForm.addControl('confirmNewPasswordFormControl', this.confirmNewPasswordFormControl);
    this.loginOneTimeForm.addControl('newPasswordEqualsFormControl', this.newPasswordEqualsFormControl);
    this.loginOneTimeForm.addControl('recaptchaFormControl', this.recaptchaFormControl);

    this.setFormValues(null);
    this.setFormValidations(false);

    this.loginOneTimeForm.get('newPasswordFormControl').valueChanges.subscribe(val => {
      this.newPasswordChanged(val);
    });

    this.loginOneTimeForm.get('confirmNewPasswordFormControl').valueChanges.subscribe(val => {
      this.newPasswordChanged(val);
    });
  }

  setFormValues(forgotPasswordInfo: ForgotPasswordInfo = null): void {
    if (forgotPasswordInfo === null) {
      forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();
    }

    this.loginOneTimeForm.setValue({
      userNameFormControl: forgotPasswordInfo.userName,
      temporaryPasswordFormControl: forgotPasswordInfo.temporaryPassword,
      newPasswordFormControl: forgotPasswordInfo.newPassword,
      confirmNewPasswordFormControl: forgotPasswordInfo.newPassword,
      newPasswordEqualsFormControl: true,
      recaptchaFormControl: null
    });
  }

  setFormValidations(clearAllValidations: boolean) {
    if (clearAllValidations) {
      this.loginOneTimeForm.get('userNameFormControl').setValidators([]);
      this.loginOneTimeForm.get('temporaryPasswordFormControl').setValidators([]);
      this.loginOneTimeForm.get('newPasswordFormControl').setValidators([]);
      this.loginOneTimeForm.get('confirmNewPasswordFormControl').setValidators([]);
      this.loginOneTimeForm.get('newPasswordEqualsFormControl').setValidators([]);
      this.loginOneTimeForm.get('recaptchaFormControl').setValidators([]);
    } else {
      this.loginOneTimeForm.get('userNameFormControl').setValidators([Validators.required]);
      this.loginOneTimeForm.get('temporaryPasswordFormControl').setValidators([Validators.required]);
      this.loginOneTimeForm
        .get('newPasswordFormControl')
        .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.passwordPattern)]);
      this.loginOneTimeForm
        .get('confirmNewPasswordFormControl')
        .setValidators([Validators.required, Validators.pattern(this.fieldValidations.userRegistration.passwordPattern)]);
      this.loginOneTimeForm.get('newPasswordEqualsFormControl').setValidators([Validators.requiredTrue]);
      this.loginOneTimeForm.get('recaptchaFormControl').setValidators([Validators.required]);
    }

    this.loginOneTimeForm.get('userNameFormControl').markAsPristine();
    this.loginOneTimeForm.get('temporaryPasswordFormControl').markAsPristine();
    this.loginOneTimeForm.get('newPasswordFormControl').markAsPristine();
    this.loginOneTimeForm.get('confirmNewPasswordFormControl').markAsPristine();
    this.loginOneTimeForm.get('newPasswordEqualsFormControl').markAsPristine();
    this.loginOneTimeForm.get('recaptchaFormControl').markAsPristine();

    this.loginOneTimeForm.get('userNameFormControl').markAsUntouched();
    this.loginOneTimeForm.get('temporaryPasswordFormControl').markAsUntouched();
    this.loginOneTimeForm.get('newPasswordFormControl').markAsUntouched();
    this.loginOneTimeForm.get('confirmNewPasswordFormControl').markAsUntouched();
    this.loginOneTimeForm.get('newPasswordEqualsFormControl').markAsUntouched();
    this.loginOneTimeForm.get('recaptchaFormControl').markAsUntouched();

    this.loginOneTimeForm.get('userNameFormControl').updateValueAndValidity();
    this.loginOneTimeForm.get('temporaryPasswordFormControl').updateValueAndValidity();
    this.loginOneTimeForm.get('newPasswordFormControl').updateValueAndValidity();
    this.loginOneTimeForm.get('confirmNewPasswordFormControl').updateValueAndValidity();
    this.loginOneTimeForm.get('newPasswordEqualsFormControl').updateValueAndValidity();
    this.loginOneTimeForm.get('recaptchaFormControl').updateValueAndValidity();
  }

  newPasswordChanged(newConfirmUserPassword: string) {
    this.loginOneTimeForm.controls['newPasswordEqualsFormControl'].setValue(this.areUserPasswordsEqual());
    this.loginOneTimeForm.controls['newPasswordEqualsFormControl'].markAsDirty();
    this.loginOneTimeForm.controls['newPasswordEqualsFormControl'].markAsTouched();
    this.loginOneTimeForm.get('newPasswordEqualsFormControl').updateValueAndValidity();
  }

  areUserPasswordsEqual(): boolean {
    return (
      this.loginOneTimeForm.controls['newPasswordFormControl'].value ===
      this.loginOneTimeForm.controls['confirmNewPasswordFormControl'].value
    );
  }

  submitEnabled(): boolean {
    return this.loginOneTimeForm.valid && !this.showExpiredTemporaryPasswordError;
  }

  resetForm(): void {
    this.showSubmitSpinner = false;
    this.showTemporaryPasswordError = false;
    this.showExpiredTemporaryPasswordError = false;
    this.showSubmitError = false;
    this.showLoginButton = false;

    this.forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();

    this.setFormValues(null);
    this.setFormValidations(false);

    this.loginOneTimeForm.markAsPristine();
    this.loginOneTimeForm.markAsUntouched();
  }

  submitUser(): void {
    if (this.loginOneTimeForm.valid) {
      this.showSubmitSpinner = true;

      this.forgotPasswordInfo = this._registrationService.getNewForgotPasswordInfo();
      this.forgotPasswordInfo.userName = this.loginOneTimeForm.get('userNameFormControl').value;
      this.forgotPasswordInfo.companyCode = this._companyService.getCompanyCode();
      this.forgotPasswordInfo.temporaryPassword = this.loginOneTimeForm.get('temporaryPasswordFormControl').value;

      this._registrationService.verifyTemporaryPassword(this.forgotPasswordInfo).subscribe(
        result => {
          if (this._logging.serviceCallTraceLog) {
            console.log('LoginOneTimeComponent.submitUser.verifyTemporaryPassword :: result.userId = ' + result.userId);
          }
          if (this._logging.serviceDataTraceLog) {
            console.log('LoginOneTimeComponent.submitUser.verifyTemporaryPassword :: result = ' + result);
          }

          if (result.userId > 0) {
            if (result.temporaryPasswordExpiredFlag !== null && result.temporaryPasswordExpiredFlag) {
              this.showSubmitSpinner = false;
              this.showTemporaryPasswordError = false;
              this.showExpiredTemporaryPasswordError = true;
              this.showSubmitError = false;
              this.showLoginButton = false;
            } else {
              this.showSubmitError = false;
              this.showTemporaryPasswordError = false;
              this.showExpiredTemporaryPasswordError = false;
              this.showLoginButton = false;

              this.forgotPasswordInfo.userId = result.userId;
              this.forgotPasswordInfo.newPassword = this.loginOneTimeForm.get('newPasswordFormControl').value;

              this._registrationService.updatePassword(this.forgotPasswordInfo).subscribe(
                result2 => {
                  if (this._logging.serviceCallTraceLog) {
                    console.log('LoginOneTimeComponent.submitUser.updatePassword :: result2 = ' + result2);
                  }
                  if (this._logging.serviceDataTraceLog) {
                    console.log('LoginOneTimeComponent.submitUser.updatePassword :: result2 = ' + result2);
                  }

                  this.showSubmitSpinner = false;

                  if (Number(result2) > 0) {
                    this.showSubmitError = false;
                    this.showTemporaryPasswordError = false;
                    this.showExpiredTemporaryPasswordError = false;
                    this.showLoginButton = true;
                  } else {
                    this.showTemporaryPasswordError = false;
                    this.showExpiredTemporaryPasswordError = false;
                    this.showSubmitError = true;
                    this.showLoginButton = false;
                    this.showSubmitSpinner = false;
                  }
                },
                error => {
                  this._handleError('submitUser.updatePassword', error, this.forgotPasswordInfo);
                  this.showSubmitSpinner = false;
                  this.showTemporaryPasswordError = false;
                  this.showExpiredTemporaryPasswordError = false;
                  this.showSubmitError = true;
                  this.showLoginButton = false;
                }
              );
            }
          } else {
            this.showSubmitSpinner = false;
            this.showTemporaryPasswordError = true;
            this.showExpiredTemporaryPasswordError = false;
            this.showSubmitError = false;
            this.showLoginButton = false;
          }
        },
        error => {
          this._handleError('submitUser.verifyTemporaryPassword', error, this.forgotPasswordInfo);
          this.showSubmitSpinner = false;
          this.showTemporaryPasswordError = true;
          this.showExpiredTemporaryPasswordError = false;
          this.showSubmitError = false;
          this.showLoginButton = false;
        }
      );
    } else {
      this.showSubmitSpinner = false;
      this.showTemporaryPasswordError = false;
      this.showExpiredTemporaryPasswordError = false;
      this.showSubmitError = true;
      this.showLoginButton = false;
    }
  }

  forgotPassword(): void {
    if (this._logging.navigationTraceLog) {
      console.log('LoginOneTimeComponent.forgotPassword');
    }
    this._router.navigate(['forgotpassword']);
  }

  openLogin(): void {
    if (this._logging.navigationTraceLog) {
      console.log('LoginOneTimeComponent.openLogin');
    }
    this._router.navigate(['login']);
  }

  scrollToTop() {
    const contentStart = document.getElementsByClassName('mat-sidenav-content');
    if (contentStart.length > 0) {
      contentStart[0].scrollTop = 0;
    }
  }
}
